import { inject, Injectable, OnDestroy } from "@angular/core";
import { Event, NavigationEnd, Router } from "@angular/router";
import { BehaviorSubject, combineLatest, Observable, Subject, takeUntil } from "rxjs";
import { environment } from "../../environments/environment";
import { HttpClient } from "@angular/common/http";
import { ApiService } from "./api.service";
import { map } from "rxjs/operators";
import { headerSports } from "../../../../desktop/src/assets/constants/market";
import { LoaderService } from "./loader.service";
import { MarketService } from "./market.service";
import { DomainService } from "./domain.service";

interface NavListApiResponse {
  result: NavList[];
  status: string;
}

export interface NavList {
  NAV_ID?: string;
  TYPE?: string;
}

@Injectable({ providedIn: "root" })
export class NavService implements OnDestroy {
  http = inject(HttpClient);
  apiService = inject(ApiService);
  openSportsBar: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  openBetSlipBar: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  openSportsPanel: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  openFavoritesPanel: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  showClass: any = false;
  public currentUrl = new BehaviorSubject<any>(undefined);
  navItems = [
    {
      name: "Home",
      path: [
        "/app/main",
        {
          outlets: {
            navigation: ["sports"],
            content: ["home", "highlights", "cricket"],
          },
        },
      ],
    },
    { name: "Casino", path: "" },
    {
      name: "In-Play",
      path: [
        "/app/main",
        {
          outlets: { navigation: ["sports"], content: ["in-play"] },
        },
      ],
    },
    {
      name: "My Markets",
      path: [
        "/app/main",
        {
          outlets: {
            navigation: ["sports"],
            content: ["my-markets"],
          },
        },
      ],
    },
    {
      name: "Soccer",
      path: [
        "/app/main",
        {
          outlets: {
            navigation: ["sports"],
            content: ["soccer"],
          },
        },
      ],
    },
    {
      name: "Cricket",
      path: [
        "/app/main",
        {
          outlets: {
            navigation: ["sports"],
            content: ["cricket"],
          },
        },
      ],
    },
    {
      name: "Tennis",
      path: [
        "/app/main",
        {
          outlets: {
            navigation: ["sports"],
            content: ["tennis"],
          },
        },
      ],
    },
  ];
  menuItems = [
    { icon: "person", name: "My Account", path: "/app/my-account" },
    { icon: "settings", name: "Betslip Settings", path: "/app/slip-settings" },
    { icon: "account_balance_wallet", name: "Transfer Wallet", path: "" },
    { icon: "sports_esports", name: "My Bets", path: "/app/bets" },
    { icon: "account_balance", name: "Account Statement", path: "/app/statement" },
    { icon: "analytics", name: "Betting Profit & Loss", path: "/app/betting-pl" },
    { icon: "lock", name: "Change Password", path: "" },
    { icon: "translate", name: "Select Language", path: "" },
    { icon: "logout", name: "Sign Out", path: "" },
  ];
  navList = new BehaviorSubject<NavList[]>([]);
  loader = inject(LoaderService);
  hiddenNavItems: NavList[] = [];
  marketService = inject(MarketService);

  router = inject(Router);
  domainService = inject(DomainService);

  // Add destroy subject for proper cleanup
  readonly destroy$ = new Subject<void>();

  get tokenResponse() {
    return this.apiService.getUserToken();
  }

  get userFields() {
    return this.tokenResponse?.user;
  }

  get userToken() {
    return this.tokenResponse?.access_token;
  }

  get domain() {
    return this.domainService.getDomain();
  }

  constructor() {
    this.router.events.pipe(takeUntil(this.destroy$)).subscribe((event: Event) => {
      if (event instanceof NavigationEnd) {
        this.currentUrl.next(event.urlAfterRedirects);
      }
    });

    this.fetchNavList()
      .pipe(takeUntil(this.destroy$))
      .subscribe((response: NavListApiResponse) => {
        if (response?.result && response?.status === "ok") {
          this.navList.next(response?.result);
          // console.log(response?.result);
          this.hiddenNavItems = response?.result;
        }
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get userId() {
    return this.apiService.getUserToken()?.user?.ID;
  }

  getOpenSportsPanel() {
    return this.openSportsPanel.asObservable();
  }

  getOpenFavoritesPanel() {
    return this.openFavoritesPanel.asObservable();
  }

  setOpenFavoritesPanel(value: boolean) {
    this.openFavoritesPanel.next(value);
  }

  setOpenSportsPanel(value: boolean) {
    this.openSportsPanel.next(value);
  }

  fetchNavList(): Observable<any> {
    return this.http.get(`${environment.sURL}${environment.endpoints.navigationList}/${this.userId}`);
  }

  isSportVisible(navId: string, navLocation: "header" | "sidenav" = "sidenav"): Observable<boolean> {
    const isLoading$ = this.loader.isLoading("/navigation/sports");

    return combineLatest([this.marketService.sports$, isLoading$]).pipe(
      map(([sports, loading]) => {
        if (loading) return false;

        const navItemId = navLocation === "header" ? this.getSportId(navId) : navId;

        // If you want "unknown id => not visible"
        if (!navItemId) return true;

        return sports.some((item) => item.id === navItemId);
      }),
    );
  }

  getSportId(name: string) {
    return headerSports.get(name)?.id || "";
  }

  openCasino() {
    const base = `https://${environment.casinoDomain}.${this.domain}/app/casino`;
    // const base = "http://localhost:8101/app/casino";

    const { CURRENCY, ID, NAME, LANGUAGE } = this.userFields ?? {};

    const qs = new URLSearchParams({
      token: this.userToken,
      tokenTimestamp: String(Date.now()),
      userFields: JSON.stringify(this.userFields),
      userId: ID,
      currency: CURRENCY,
      displayName: NAME,
      lang: LANGUAGE,
    });

    window.open(`${base}?${qs.toString()}`, "_blank", "noopener,noreferrer");
  }
}
